import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertCircle, User } from 'lucide-react';

interface NameEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  onUpdate: (newName: string, reason: string) => void;
}

export function NameEditDialog({ 
  isOpen, 
  onClose, 
  currentName, 
  onUpdate 
}: NameEditDialogProps) {
  const [newName, setNewName] = useState(currentName);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newName.trim()) {
      return;
    }
    
    if (newName.trim() === currentName) {
      onClose();
      return;
    }
    
    if (!reason.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      await onUpdate(newName.trim(), reason.trim());
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewName(currentName);
    setReason('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Edit Patient Name
          </DialogTitle>
          <DialogDescription>
            Update the patient's name. This change will be logged for audit purposes.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-name">Current Name</Label>
            <Input
              id="current-name"
              value={currentName}
              disabled
              className="bg-gray-50"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="new-name">New Name *</Label>
            <Input
              id="new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter the correct name"
              required
              autoFocus
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Change *</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why the name needs to be changed (e.g., 'Typo in original entry', 'Patient provided correct spelling')"
              required
              rows={3}
            />
          </div>
          
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Important:</p>
              <p>This change will be permanently recorded and cannot be undone. Please ensure the new name is correct.</p>
            </div>
          </div>
        </form>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isLoading || !newName.trim() || !reason.trim() || newName.trim() === currentName}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? 'Updating...' : 'Update Name'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
